'use client';
import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { FormLabel } from './FormLabel';

export const FIELD_WIDTH = { xs: '100%', sm: '32%' };
export const FIELD_SPACING = 2;

interface DetailFieldProps {
  label: string | React.ReactNode;
  value: React.ReactNode;
  isReadOnly?: boolean;
  children?: React.ReactNode;
  width?: any;
  /** Whether this field is required (cannot save without it) */
  required?: boolean;
  /** Whether this field is required to submit (can save as pending, but cannot submit without it) */
  requiredToSubmit?: boolean;
  /** Error message to display (shown even in readonly mode) */
  error?: string;
}

const DetailField: FC<DetailFieldProps> = ({
  label,
  value,
  isReadOnly,
  children,
  width,
  required,
  requiredToSubmit,
  error,
}) => {
  const renderLabel = () => {
    if (typeof label === 'string') {
      return (
        <FormLabel
          required={required}
          requiredToSubmit={requiredToSubmit}
          optional={(!required && !requiredToSubmit) ?? false}
          isEditMode={!isReadOnly}
        >
          {label}
        </FormLabel>
      );
    }
    return label;
  };

  return (
    <Box sx={{ width: width ?? FIELD_WIDTH }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {renderLabel()}
      </Typography>
      {isReadOnly ? (
        <>
          {typeof value === 'string' || typeof value === 'number' ? (
            <Typography
              variant="body1"
              component="span"
              sx={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                display: 'block',
                color: error ? 'error.main' : undefined,
              }}
            >
              {value}
            </Typography>
          ) : (
            <Box
              sx={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                color: error ? 'error.main' : 'text.primary',
                typography: 'body1',
                '& a': { color: 'primary.main' },
              }}
            >
              {value}
            </Box>
          )}
          {error ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {error}
            </Typography>
          ) : null}
        </>
      ) : (
        children
      )}
    </Box>
  );
};

export default DetailField;
